import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';

const SKELETON_THEME = {
    baseColor: '#e8ecf2',
    highlightColor: '#f0f3f8',
};

const WEEKDAY_LABELS = 7;
const MONTH_GRID_ROWS = 6;
const TIME_GRID_ROWS = 8;
const AGENDA_DAY_GROUPS = 4;

/**
 * @param {{ variant: 'month' | 'time' | 'agenda' }} props
 */
export function EventCalendarSkeleton({ variant }) {
    return (
        <SkeletonTheme
            baseColor={SKELETON_THEME.baseColor}
            highlightColor={SKELETON_THEME.highlightColor}
        >
            <div
                className={`event-calendar-skeleton event-calendar-skeleton--${variant}`}
                aria-hidden
            >
                <div className="event-calendar-skeleton-toolbar">
                    <div className="event-calendar-skeleton-toolbar-group">
                        <Skeleton width={36} height={34} borderRadius={8} />
                        <Skeleton width={36} height={34} borderRadius={8} />
                        {variant === 'month' ? (
                            <Skeleton
                                width={72}
                                height={34}
                                borderRadius={8}
                                className="event-calendar-skeleton-toolbar-today"
                            />
                        ) : null}
                    </div>
                    <Skeleton
                        width={variant === 'agenda' ? 140 : 180}
                        height={28}
                        borderRadius={6}
                        className="event-calendar-skeleton-toolbar-title"
                    />
                    <div className="event-calendar-skeleton-toolbar-group event-calendar-skeleton-toolbar-group--end">
                        {variant === 'agenda' ? (
                            <Skeleton width={64} height={34} borderRadius={8} />
                        ) : variant === 'month' ? (
                            <>
                                <Skeleton width={52} height={34} borderRadius={8} />
                                <Skeleton width={52} height={34} borderRadius={8} />
                                <Skeleton width={52} height={34} borderRadius={8} />
                            </>
                        ) : (
                            <Skeleton width={64} height={34} borderRadius={8} />
                        )}
                    </div>
                </div>

                {variant === 'agenda' ? (
                    <AgendaSkeleton />
                ) : variant === 'time' ? (
                    <TimeGridSkeleton />
                ) : (
                    <MonthGridSkeleton />
                )}
            </div>
        </SkeletonTheme>
    );
}

function MonthGridSkeleton() {
    return (
        <div className="event-calendar-skeleton-grid-wrap">
            <div className="event-calendar-skeleton-weekdays">
                {Array.from({ length: WEEKDAY_LABELS }, (_, i) => (
                    <Skeleton
                        key={`wd-${i}`}
                        height={16}
                        borderRadius={4}
                    />
                ))}
            </div>
            <div className="event-calendar-skeleton-month-grid">
                {Array.from({ length: MONTH_GRID_ROWS }, (_, row) => (
                    <div
                        key={`row-${row}`}
                        className="event-calendar-skeleton-month-row"
                    >
                        {Array.from({ length: WEEKDAY_LABELS }, (_, col) => {
                            const idx = row * WEEKDAY_LABELS + col;
                            const showEvent = idx % 5 === 2 || idx % 7 === 4;
                            return (
                                <div
                                    key={`cell-${row}-${col}`}
                                    className="event-calendar-skeleton-month-cell"
                                >
                                    <Skeleton
                                        width={22}
                                        height={14}
                                        borderRadius={4}
                                        className="event-calendar-skeleton-day-num"
                                    />
                                    {showEvent ? (
                                        <Skeleton
                                            height={18}
                                            borderRadius={4}
                                            className="event-calendar-skeleton-event-pill"
                                        />
                                    ) : null}
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        </div>
    );
}

function TimeGridSkeleton() {
    return (
        <div className="event-calendar-skeleton-grid-wrap">
            <div className="event-calendar-skeleton-weekdays event-calendar-skeleton-weekdays--time">
                <span aria-hidden />
                {Array.from({ length: WEEKDAY_LABELS }, (_, i) => (
                    <Skeleton
                        key={`wd-${i}`}
                        height={16}
                        borderRadius={4}
                    />
                ))}
            </div>
            <div className="event-calendar-skeleton-time-grid">
                {Array.from({ length: TIME_GRID_ROWS }, (_, row) => (
                    <div
                        key={`slot-${row}`}
                        className="event-calendar-skeleton-time-row"
                    >
                        <Skeleton
                            width={42}
                            height={12}
                            borderRadius={4}
                        />
                        <div className="event-calendar-skeleton-time-columns">
                            {Array.from({ length: WEEKDAY_LABELS }, (_, col) => (
                                <div
                                    key={`col-${row}-${col}`}
                                    className="event-calendar-skeleton-time-cell"
                                >
                                    {row === 2 && col === 2 ? (
                                        <Skeleton
                                            height={56}
                                            borderRadius={6}
                                            className="event-calendar-skeleton-time-event"
                                        />
                                    ) : null}
                                    {row === 4 && col === 5 ? (
                                        <Skeleton
                                            height={40}
                                            borderRadius={6}
                                            className="event-calendar-skeleton-time-event"
                                        />
                                    ) : null}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function AgendaSkeleton() {
    return (
        <ul className="event-calendar-skeleton-agenda list-unstyled mb-0">
            {Array.from({ length: AGENDA_DAY_GROUPS }, (_, dayIndex) => (
                <li
                    key={`day-${dayIndex}`}
                    className="event-calendar-skeleton-agenda-day"
                >
                    <Skeleton
                        width={dayIndex % 2 === 0 ? '68%' : '58%'}
                        height={18}
                        borderRadius={4}
                        className="event-calendar-skeleton-agenda-date"
                    />
                    {Array.from(
                        { length: dayIndex % 3 === 0 ? 2 : 1 },
                        (_, eventIndex) => (
                            <div
                                key={`event-${dayIndex}-${eventIndex}`}
                                className="event-calendar-skeleton-agenda-item"
                            >
                                <Skeleton
                                    width={72}
                                    height={12}
                                    borderRadius={4}
                                />
                                <Skeleton
                                    height={16}
                                    borderRadius={4}
                                    style={{ marginTop: 8 }}
                                />
                            </div>
                        ),
                    )}
                </li>
            ))}
        </ul>
    );
}
